<template>
  <v-card class="pdfContainer noOverflow" v-touch="handleGesture" :style="backgroundColor">
    <div class="pageCount" v-if="false">{{ currentPageNumber }}/{{ pageCount }}</div>
    <v-snackbar top v-model="isSnackbar">
      {{ $t("components.files.PdfViewer.snackBarTitle") }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="isSnackbar = false">
          {{ $t("components.files.PdfViewer.snackBarButton") }}
        </v-btn>
      </template>
    </v-snackbar>
    <v-progress-circular indeterminate color="grey" v-if="currentLoading" class="loadingIcon" data-test-pdf-loading />
    <div class="pdfFlexColumnContainer">
      <div class="pdfFlexColumnUpper">
        <div class="pdfFlexRowContainer">
          <div class="pdfFlexRowSmallerUp" v-if="pageCount !== 1 && mdAndUp">
            <v-img
              v-if="previousPage && !previousLoading"
              :src="previousPage"
              alt="Previous Page"
              class="image"
              contain
              @click="openPreviousPage"
            />
          </div>
          <div :class="pageCount !== 1 ? (mdAndUp ? 'pdfFlexRowLargerUp' : 'pdfFlexRowLargerDown') : 'pdfFlexRowOnly'">
            <v-img
              v-if="currentPage && !currentLoading"
              img
              :src="currentPage"
              alt="Current Page"
              class="image"
              contain
              @click="exportPDF"
            />
          </div>
          <div class="pdfFlexRowSmallerUp" v-if="pageCount !== 1 && mdAndUp">
            <v-img
              :src="nextPage"
              alt="Next Page"
              v-if="nextPage && !nextLoading"
              class="image"
              contain
              @click="openNextPage"
            />
          </div>
        </div>
      </div>
      <div class="pdfFlexColumnLower" v-if="!mdAndUp">
        <div class="pdfFlexRowContainer">
          <div class="pdfFlexRowSmallerDown" v-if="pageCount !== 1">
            <v-img
              v-if="previousPage && !previousLoading"
              :src="previousPage"
              alt="Previous Page"
              class="image"
              contain
              @click="openPreviousPage"
            />
          </div>
          <div class="pdfFlexRowSmallerDown" v-if="pageCount !== 1">
            <v-img
              v-if="nextPage && !nextLoading"
              :src="nextPageNumber"
              alt="Next Page"
              class="image"
              contain
              @click="openNextPage"
            />
          </div>
        </div>
      </div>
    </div>

    <v-card-actions class="buttonContainer">
      <v-row class="mt-6">
        <v-spacer></v-spacer>
        <v-btn
          v-if="mdAndUp && pageCount !== 1"
          @click="openPreviousPage"
          :disabled="pageCount === 1"
          class="button wide"
          elevation="0"
          x-large
        >
          <v-icon>mdi-arrow-left</v-icon>
          {{ $t("components.files.PdfViewer.lastPage") }}
        </v-btn>
        <v-btn
          v-else-if="pageCount !== 1"
          :disabled="pageCount === 1"
          class="button"
          rounded
          outlined
          fab
          small
          @click="openPreviousPage"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="mdAndUp"
          color="success"
          class="button wide"
          elevation="0"
          x-large
          style="width: 200px"
          data-test-confirm-pdf-upload
          @click="exportPDF"
        >
          {{ $t("components.files.PdfViewer.select") }}
          <v-icon>mdi-check</v-icon>
        </v-btn>
        <v-btn
          v-else
          color="success"
          class="button"
          rounded
          outlined
          fab
          small
          data-test-confirm-pdf-upload
          @click="exportPDF"
        >
          <v-icon>mdi-check</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="mdAndUp && pageCount !== 1"
          :disabled="pageCount === 1"
          class="button wide"
          elevation="0"
          x-large
          @click="openNextPage"
        >
          {{ $t("components.files.PdfViewer.nextPage") }}
          <v-icon>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-else-if="pageCount !== 1"
          :disabled="pageCount === 1"
          class="button"
          rounded
          outlined
          fab
          small
          @click="openNextPage"
        >
          <v-icon>mdi-arrow-right</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { convertUrlToFile } from "@/lib/files/PdfHelper";
import { convertPdfToImagePromises } from "@/lib/utility/convertPdfToImage";
import { handleError } from "@/lib/utility/handleError";
import { getPdfjsWorkerSrc } from "@/lib/utility/pdfjs-worker";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { Component, Prop } from "vue-property-decorator";
const pdfJs = () => import("pdfjs-dist/legacy/build/pdf.mjs");

@Component({
  components: {}
})
export default class PdfViewer extends DarkModeHighlightMixin {
  /**
   * The pdf file to display
   */
  @Prop()
  pdf!: File;

  /**
   * A list of promises that each return a page of the pdf as an image
   */
  images: Promise<string>[] = [];

  /**
   * previous page
   */
  previousPage = "";

  /**
   * current page
   */
  currentPage = "";

  /**
   * next page
   */
  nextPage = "";

  /**
   * is snackbar visible
   */
  isSnackbar = true;

  /**
   * The currently displayed page
   */
  currentPageNumber = 0;

  /**
   * Flag if current page is loading
   */
  currentLoading = true;

  /**
   * Flag if previous page is loading
   */
  previousLoading = true;

  /**
   * Flag if next page is loading
   */
  nextLoading = true;

  /**
   * amount of pages in pdf
   */
  get pageCount() {
    return this.images.length;
  }

  /**
   * get the number of the next page
   */
  get nextPageNumber() {
    let nextPage = this.currentPageNumber + 1;

    if (this.pageCount - 1 < nextPage) {
      nextPage = 0;
    }

    return nextPage;
  }

  /**
   * Get the number of the previous page
   */
  get previousPageNumber() {
    let nextPage = this.currentPageNumber - 1;

    if (nextPage < 0) {
      nextPage = this.pageCount - 1;
    }

    return nextPage;
  }

  /**
   * Get if display is large
   */
  get mdAndUp() {
    return this.$vuetify.breakpoint.mdAndUp;
  }

  /**
   * Handles swiping gestures
   */
  get handleGesture() {
    return {
      right: () => this.openPreviousPage(),
      left: () => this.openNextPage()
    };
  }

  /**
   * Converts each page of the pdf to an image
   */
  async mounted() {
    if (!this.pdf) {
      return;
    }

    const pdfjsLib = await pdfJs();
    pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfjsWorkerSrc();

    const pdf: Uint8Array = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(this.pdf);
      reader.onload = (e) => {
        resolve(new Uint8Array(e.target?.result as any));
      };
    });

    const images = await convertPdfToImagePromises(pdf);
    this.images.splice(0, this.images.length, ...images);

    this.showPdfAsImage();

    document.addEventListener("keyup", this.handleKeyDown);
  }

  /**
   * removes key event listener
   */
  beforeDestroy() {
    document.removeEventListener("keyup", this.handleKeyDown);
  }

  /**
   * navigate the pdf using keys
   */
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === "ArrowLeft") {
      this.openPreviousPage();
    }

    if (event.key === "ArrowRight") {
      this.openNextPage();
    }

    if (event.key === "Enter" || event.key === " ") {
      this.exportPDF();
    }
  }

  /**
   * Opens next page (or first when on last)
   */
  openNextPage() {
    if (this.pageCount === 1) {
      return;
    }

    this.currentPageNumber = this.nextPageNumber;

    this.showPdfAsImage();
  }

  /**
   * opens previous page (or last when on first)
   */
  openPreviousPage() {
    if (this.pageCount === 1) return;

    this.currentPageNumber = this.previousPageNumber;

    this.showPdfAsImage();
  }

  /**
   * sets current/previous/next pages of pdf
   */
  showPdfAsImage() {
    this.currentLoading = true;
    this.previousLoading = true;
    this.nextLoading = true;

    const previous = this.images[this.previousPageNumber];
    const current = this.images[this.currentPageNumber];
    const next = this.images[this.nextPageNumber];

    if (previous) {
      previous
        .then((v) => {
          this.previousLoading = false;
          this.previousPage = v;
        })
        .catch(handleError);
    }
    if (current) {
      current
        .then((v) => {
          this.currentLoading = false;
          this.currentPage = v;
        })
        .catch(handleError);
    }
    if (next) {
      next
        .then((v) => {
          this.nextLoading = false;
          this.nextPage = v;
        })
        .catch(handleError);
    }
  }

  /**
   * converts current page to image
   */
  async exportPDF() {
    if (this.currentPage) {
      const file = await convertUrlToFile(
        this.currentPage,
        `${this.pdf.name}_${this.currentPageNumber}.jpg`,
        "image/jpg"
      );
      this.$emit("onImageExport", file);
    }
  }
}
</script>
<style scoped>
:root {
  --main-height: 75vh;
}

.image {
  height: 100%;
  width: 100%;
  margin-top: auto;
  margin-bottom: auto;
  object-fit: contain;
}

.pdfFlexColumnContainer {
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: var(--main-height);
  margin-top: 10vh;
  margin-bottom: 15vh;
}

.pdfFlexColumnUpper {
  flex: 1;
}

.pdfFlexColumnLower {
  flex: 0.5;
}

.pdfFlexRowContainer {
  display: flex;
  flex-direction: row;
}

.pdfFlexRowSmallerUp {
  width: calc(100vw * 1 / 6);
  margin-left: calc((100vw * 1 / 5 - 100vw * 1 / 6) / 2);
  margin-right: calc((100vw * 1 / 5 - 100vw * 1 / 6) / 2);
  height: 75vh;
  flex: 0.5;
  overflow: hidden;
}

.pdfFlexRowLargerUp {
  width: calc(100vw * 3 / 5);
  height: 75vh;
  flex: 1;
  overflow: hidden;
}

.pdfFlexRowSmallerDown {
  margin-top: calc((75vh * 1 / 3 - 75vh * 1 / 4) / 2);
  width: calc(100vw * 1 / 2);
  height: calc(75vh * 1 / 4);
  flex: 0.5;
  overflow: hidden;
}

.pdfFlexRowLargerDown {
  width: calc(100vw);
  height: calc(75vh * 2 / 3);
  flex: 1;
  overflow: hidden;
}

.pdfFlexRowOnly {
  width: calc(100vw);
  height: calc(75vh);
  flex: 1;
  overflow: hidden;
}

.loadingIcon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.hidden {
  visibility: hidden;
}

.original {
  z-index: -1;
}

.noOverflow {
  position: absolute !important;
  left: 0;
  top: 0;
  height: 100vh !important;
  width: 100vw !important;
  overflow: hidden !important;
}

.pdfContainer {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.pageCount {
  position: absolute;
  z-index: 1;
  top: 40px;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

.buttonContainer {
  height: 15vh;
  width: 100vw;
  position: absolute;
  bottom: 0;
  left: 0;
  margin: auto;
}

.wide {
  width: 200px;
}
</style>
